class CTEmbroideryCustomizer {
  constructor(root, options = {}) {
    this.root = root;
    this.toggle = root.querySelector('[data-ct-toggle]');
    this.builder = root.querySelector('[data-ct-builder]');
    this.error = root.querySelector('[data-ct-error]');
    this.properties = Object.fromEntries([...root.querySelectorAll('[data-ct-property]')].map((el) => [el.dataset.ctProperty, el]));
    this.toggle.addEventListener('change', () => this.onToggle());
    root.addEventListener('change', (event) => this.onChange(event));
    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-ct-monogram-letter], [data-ct-wedding-letter]')) {
        event.target.value = event.target.value.replace(/[^a-z]/gi, '').toUpperCase();
        if (event.target.matches('[data-ct-monogram-letter]')) this.syncMonogramInput();
      }
      if (event.target.matches('[data-ct-personalization], [data-ct-monogram-letter], [data-ct-wedding-letter], [data-ct-wedding-year]')) this.update();
    });
    root.querySelector('[data-ct-edit]')?.addEventListener('click', () => root.querySelector('[data-ct-type]:checked')?.focus());
    this.form = document.getElementById(root.dataset.formId);
    if (!options.skipSubmit) {
      this.form?.addEventListener('submit', (event) => this.validate(event), true);
      this.form?.addEventListener('submit', (event) => this.addCustomizedItems(event), true);
    }
    this.form?.addEventListener('variant:change', (event) => {
      this.setCoverColor(event.detail?.variant?.options || event.detail?.variant?.title || '');
    });
    this.setCoverColor(root.dataset.initialVariant || '');
  }

  onToggle() {
    const enabled = this.toggle.checked;
    this.builder.hidden = !enabled;
    Object.values(this.properties).forEach((input) => { input.disabled = !enabled; });
    this.properties.enabled.value = enabled ? 'Yes' : '';
    this.error.hidden = true;
    if (!enabled) this.clearRequired();
    this.update();
  }

  onChange(event) {
    if (event.target.matches('[data-ct-type]')) this.selectType(event.target.value);
    if (event.target.matches('[data-ct-design]')) this.selectDesign(event.target.value);
    if (event.target.matches('[data-ct-monogram-layout]')) this.selectMonogramLayout(event.target);
    this.update();
  }

  selectType(type) {
    this.root.querySelector('[data-ct-design-step]').hidden = false;
    this.root.querySelectorAll('[data-ct-panel]').forEach((panel) => { panel.hidden = panel.dataset.ctPanel !== type; });
    this.root.querySelectorAll('[data-ct-panel] input:not([type="radio"]), [data-ct-panel] select').forEach((field) => { field.required = false; });
    const activePanel = this.root.querySelector(`[data-ct-panel="${type}"]`);
    this.root.querySelectorAll('[data-ct-design]').forEach((input) => {
      if (!activePanel.contains(input)) input.checked = false;
    });
    if (type === 'Established') {
      const establishedDesign = activePanel.querySelector('[data-ct-design]');
      establishedDesign.checked = true;
      activePanel.querySelectorAll('[data-ct-wedding-letter]').forEach((field) => { field.required = true; });
      activePanel.querySelector('[data-ct-wedding-year]').required = true;
      this.selectDesign(establishedDesign.value);
    }
    this.root.querySelector('[data-ct-color-step]').hidden = false;
    this.root.querySelector('[data-ct-placement-step]').hidden = false;
  }

  selectDesign(design) {
    const dogField = this.root.querySelector('[data-ct-dog-field]');
    const placeField = this.root.querySelector('[data-ct-place-field]');
    dogField.hidden = design !== 'Dog';
    this.root.querySelectorAll('[data-ct-dog-breed]').forEach((field) => { field.required = design === 'Dog'; });
    if (placeField) {
      const isPlace = ['ZIP code', 'Latitude + longitude'].includes(design);
      placeField.hidden = !isPlace;
      const input = placeField.querySelector('input');
      input.required = isPlace;
      const label = placeField.querySelector('[data-ct-place-label]');
      if (design === 'ZIP code') { label.textContent = 'ZIP code'; input.placeholder = 'e.g. 90254'; input.maxLength = 5; }
      if (design === 'Latitude + longitude') {
        label.textContent = 'Location for coordinates';
        input.placeholder = 'Street address, landmark or meaningful place';
        input.maxLength = 120;
      }
    }
    const coordinateHelp = this.root.querySelector('[data-ct-coordinate-help]');
    if (coordinateHelp) coordinateHelp.hidden = design !== 'Latitude + longitude';
    this.updatePlacementAvailability(design);
    const isMonogram = design.startsWith('Monogram style');
    const inputWrap = this.root.querySelector('[data-ct-monogram-input]');
    if (inputWrap) inputWrap.hidden = !isMonogram;
    const layoutsWrap = this.root.querySelector('[data-ct-monogram-layouts]');
    const layoutKind = design.includes('Standard') ? 'standard' : design.includes('Circle') ? 'circle' : '';
    if (layoutsWrap) layoutsWrap.hidden = !layoutKind;
    this.root.querySelectorAll('[data-ct-layout-group]').forEach((group) => { group.hidden = group.dataset.ctLayoutGroup !== layoutKind; });
    this.root.querySelectorAll('[data-ct-monogram-layout]').forEach((field) => {
      field.required = Boolean(layoutKind && field.closest('[data-ct-layout-group]')?.dataset.ctLayoutGroup === layoutKind);
      if (!field.required) field.checked = false;
    });
    this.root.querySelectorAll('[data-ct-monogram-letter]').forEach((field) => { field.required = isMonogram && !layoutKind; field.closest('label').hidden = false; });
    const order = this.root.querySelector('[data-ct-monogram-order]');
    if (order && isMonogram) {
      const settings = {
        'Monogram style 1 — Standard': {
          labels: ['Left', 'Center', 'Right'],
          guidance: 'Choose a two- or three-letter arrangement, then enter the letters exactly where you want them to appear.',
          arrangement: 'Choose an arrangement above',
        },
        'Monogram style 2 — Stacked': {
          labels: ['Top left', 'Bottom left', 'Large right'],
          guidance: 'Enter the letters according to their position. The large right initial traditionally represents the last name.',
          arrangement: 'Top left · Bottom left · Large right',
        },
        'Monogram style 4 — Circle': {
          labels: ['Left', 'Center', 'Right'],
          guidance: 'Choose a two- or three-letter arrangement, then enter the letters exactly where you want them to appear.',
          arrangement: 'Choose an arrangement above',
        },
      }[design];
      this.root.querySelectorAll('[data-ct-letter-label]').forEach((label, index) => { label.textContent = settings.labels[index]; });
      order.textContent = settings.guidance;
      this.root.querySelector('[data-ct-monogram-arrangement]').textContent = settings.arrangement;
      this.root.querySelector('[data-ct-monogram-confirm]').checked = false;
      this.syncMonogramInput();
    }
  }

  selectMonogramLayout(field) {
    const count = Number(field.dataset.letterCount || 3);
    const design = this.value('[data-ct-design]');
    const labels = count === 2 ? ['Left', 'Right'] : ['Left', 'Center', 'Right'];
    const fields = [...this.root.querySelectorAll('[data-ct-monogram-letter]')];
    fields.forEach((input, index) => {
      const visible = index < count;
      input.closest('label').hidden = !visible;
      input.required = visible;
      if (!visible) input.value = '';
      const label = input.closest('label').querySelector('[data-ct-letter-label]');
      if (visible) label.textContent = labels[index];
    });
    const largeCenter = field.value.includes('large center');
    const guidance = largeCenter
      ? 'The larger center initial traditionally represents the last name—whether the monogram is for an individual, couple or family.'
      : 'Enter the letters according to where you want them to appear. We will embroider them exactly as shown.';
    this.root.querySelector('[data-ct-monogram-order]').textContent = guidance;
    this.root.querySelector('[data-ct-monogram-arrangement]').textContent = `${design.includes('Circle') ? 'Circle' : 'Standard'} · ${field.value}`;
    this.root.querySelector('[data-ct-monogram-confirm]').checked = false;
    this.syncMonogramInput();
  }

  syncMonogramInput() {
    const fields = [...this.root.querySelectorAll('[data-ct-monogram-letter]')].filter((field) => !field.closest('label').hidden);
    const letters = fields.map((field) => field.value.toUpperCase());
    const combined = letters.join('');
    const hidden = this.root.querySelector('[data-ct-panel="Monogram"] [data-ct-personalization]');
    if (hidden) hidden.value = combined;
    const confirmWrap = this.root.querySelector('[data-ct-monogram-confirm-wrap]');
    const summary = this.root.querySelector('[data-ct-monogram-summary]');
    if (confirmWrap) confirmWrap.hidden = letters.some((letter) => !letter);
    if (summary) summary.textContent = combined ? `Your initials: ${letters.join(' · ')}` : '';
  }

  setCoverColor(value) {
    const text = Array.isArray(value) ? value.join(' ') : String(value);
    this.coverColor = ['Lagoon', 'Poppy', 'Shadow'].find((color) => text.toLowerCase().includes(color.toLowerCase())) || this.coverColor || '';
    this.updatePreview();
  }

  updatePlacementAvailability(design) {
    const coordinates = design === 'Latitude + longitude';
    this.root.querySelectorAll('[data-ct-placement]').forEach((input) => {
      const isCorner = input.value.startsWith('Corner');
      input.disabled = coordinates && isCorner;
      input.closest('label')?.classList.toggle('is-disabled', input.disabled);
      if (input.disabled && input.checked) input.checked = false;
    });
    const note = this.root.querySelector('[data-ct-placement-note]');
    if (note) note.hidden = !coordinates;
  }

  updatePreview() {
    const preview = this.root.querySelector('[data-ct-preview]');
    if (!preview || !this.toggle.checked) return;
    const design = this.value('[data-ct-design]');
    const type = this.value('[data-ct-type]');
    const threadInput = this.root.querySelector('[data-ct-thread]:checked');
    const personalization = this.personalization(type, design);
    const initialVariant = (this.root.dataset.initialVariant || '').toLowerCase();
    const coverColor = this.coverColor || ['Lagoon', 'Poppy', 'Shadow'].find((color) => initialVariant.includes(color.toLowerCase())) || 'Poppy';
    const swatchUrl = this.root.getAttribute(`data-swatch-${coverColor.toLowerCase()}`) || this.root.getAttribute('data-swatch-poppy') || '';
    preview.hidden = !(swatchUrl && design && threadInput);
    if (preview.hidden) return;

    const fabric = this.root.querySelector('[data-ct-fabric-preview]');
    const art = this.root.querySelector('[data-ct-preview-art]');
    const dogAssetKeys = {
      Doodle: 'dogDoodle',
      Labrador: 'dogLabrador',
      'Golden Retriever': 'dogGoldenRetriever',
      Dachshund: 'dogDachshund',
      'Cavalier King Charles': 'dogCavalierKingCharles',
      'French Bulldog': 'dogFrenchBulldog',
    };
    let previewImage = design === 'Dog' ? this.root.dataset[dogAssetKeys[personalization]] : '';
    if (design === 'Mahjong') previewImage = this.root.dataset.iconMahjong;
    if (design === 'Pickleball') previewImage = this.root.dataset.iconPickleball;
    const monogramLayout = this.value('[data-ct-monogram-layout]');
    if (design === 'Monogram style 1 — Standard') {
      if (monogramLayout === 'Three letters — same size') previewImage = this.root.dataset.monoStandardSame;
      else if (monogramLayout === 'Two letters — same size') previewImage = this.root.dataset.monoStandardTwo;
      else previewImage = this.root.dataset.monoStandard;
    }
    if (design === 'Monogram style 2 — Stacked') previewImage = this.root.dataset.monoStacked;
    if (design === 'Monogram style 4 — Circle') previewImage = monogramLayout === 'Two letters' ? this.root.dataset.monoCircleTwo : this.root.dataset.monoCircle;
    if (design === 'Milestone Monogram — EST. + year') previewImage = this.root.dataset.established;
    if (design === 'ZIP code') previewImage = this.root.dataset.zipExample;
    if (design === 'Latitude + longitude') previewImage = this.root.dataset.coordinatesExample;
    const monogramStyle = {
      'Monogram style 1 — Standard': 'standard',
      'Monogram style 2 — Stacked': 'stacked',
      'Monogram style 4 — Circle': 'circle',
      'Milestone Monogram — EST. + year': 'established',
    }[design] || '';
    const usesImage = Boolean(previewImage);
    const usesFixedColors = design === 'Pickleball';
    const usesGlyphs = false;
    const usesExampleArtwork = Boolean(monogramStyle || ['ZIP code', 'Latitude + longitude'].includes(design));
    const usesMonogramSample = Boolean(monogramStyle);
    let previewText = personalization || design;
    if (monogramStyle === 'established') previewText = personalization.replace(' — ', '\n');
    fabric.style.backgroundImage = `url("${swatchUrl}")`;
    art.style.color = getComputedStyle(threadInput.closest('label').querySelector('span')).backgroundColor;
    art.textContent = usesImage || usesGlyphs ? '' : previewText;
    art.classList.toggle('ct-fabric-preview__art--image', usesImage);
    art.classList.toggle('ct-fabric-preview__art--fixed-color', usesFixedColors);
    art.classList.toggle('ct-fabric-preview__art--monogram-sample', usesMonogramSample);
    art.classList.toggle('ct-fabric-preview__art--text', !usesImage && (Boolean(personalization) || ['ZIP code', 'Latitude + longitude'].includes(design)));
    ['standard', 'stacked', 'circle', 'established'].forEach((style) => {
      art.classList.toggle(`ct-fabric-preview__art--monogram-${style}`, monogramStyle === style);
    });
    art.style.setProperty('--preview-image', usesImage ? `url("${previewImage}")` : 'none');
    this.root.querySelector('[data-ct-preview-cover]').textContent = `${coverColor} fabric`;
    const previewPersonalization = this.root.querySelector('[data-ct-preview-personalization]');
    const coordinateRequest = design === 'Latitude + longitude';
    const zipRequest = design === 'ZIP code';
    previewPersonalization.hidden = !usesExampleArtwork;
    if (usesMonogramSample) previewPersonalization.textContent = 'Digital preview of your selected color combination. Sample initials shown—we’ll stitch yours as entered above.';
    if (zipRequest) previewPersonalization.textContent = `Your ZIP code: ${personalization}. Example numbers are shown above; your submitted ZIP code will be embroidered in this style.`;
    if (coordinateRequest) previewPersonalization.textContent = `Location request: ${personalization}. We’ll determine the coordinates, format them as shown, and send the final line for your approval before embroidery begins.`;
  }

  glyphUrl(character) {
    const key = character === '.' ? 'decimal' : character === '°' ? 'degree' : character.toLowerCase();
    return `https://cdn.shopify.com/s/files/1/0613/3271/7820/files/standard-${key}.png`;
  }

  renderGlyphPreview(art, text, design) {
    const wrap = document.createElement('span');
    const isStandard = design === 'Monogram style 1 — Standard';
    const isStacked = design === 'Monogram style 2 — Stacked';
    wrap.className = `ct-glyph-preview ${isStacked ? 'ct-glyph-preview--stacked' : isStandard ? 'ct-glyph-preview--monogram' : 'ct-glyph-preview--location'}`;
    const layout = this.value('[data-ct-monogram-layout]');
    [...text].forEach((character, index) => {
      if (character === ' ') {
        const spacer = document.createElement('span');
        spacer.className = 'ct-glyph-preview__space';
        wrap.appendChild(spacer);
        return;
      }
      if (!/[a-z0-9.°]/i.test(character)) return;
      const glyph = document.createElement('span');
      glyph.className = 'ct-glyph-preview__glyph';
      if (layout.includes('large center') && index === 1) glyph.classList.add('is-large');
      if (isStacked) glyph.classList.add(`is-stacked-${index + 1}`);
      glyph.style.setProperty('--glyph-image', `url("${this.glyphUrl(character)}")`);
      wrap.appendChild(glyph);
    });
    art.appendChild(wrap);
  }

  clearRequired() {
    this.root.querySelectorAll('input, select').forEach((field) => { if (!field.matches('[data-ct-toggle]')) field.required = false; });
  }

  value(selector) { return this.root.querySelector(`${selector}:checked`)?.value || ''; }

  validate(event) {
    if (!this.toggle.checked) return true;
    this.update();
    if (this.complete) {
      this.error.hidden = true;
      return true;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.error.hidden = false;
    this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstMissing = this.root.querySelector('input[required]:invalid, select[required]:invalid') || this.root.querySelector('[data-ct-type]');
    firstMissing?.focus({ preventScroll: true });
    return false;
  }

  personalization(type, design) {
    if (type === 'Established') {
      const initials = [...this.root.querySelectorAll('[data-ct-wedding-letter]')].map((field) => field.value.trim().toUpperCase()).join('');
      const year = this.root.querySelector('[data-ct-wedding-year]').value.trim();
      return [initials, year && `EST. ${year}`].filter(Boolean).join(' — ');
    }
    if (design === 'Dog') return this.root.querySelector('[data-ct-dog-breed]:checked')?.value || '';
    const activePanel = this.root.querySelector(`[data-ct-panel="${type}"]`);
    return activePanel?.querySelector('[data-ct-personalization]')?.value.trim().toUpperCase() || '';
  }

  update() {
    if (!this.toggle.checked) return;
    const type = this.value('[data-ct-type]');
    let design = this.value('[data-ct-design]');
    if (type === 'Established') design = 'Milestone Monogram — EST. + year';
    const thread = this.value('[data-ct-thread]');
    this.updatePlacementAvailability(design);
    const placement = this.value('[data-ct-placement]');
    const personalization = this.personalization(type, design);
    this.properties.type.value = type;
    const monogramLayout = this.value('[data-ct-monogram-layout]');
    this.properties.design.value = monogramLayout ? `${design} — ${monogramLayout}` : design;
    this.properties.personalization.value = personalization;
    this.properties.thread.value = thread;
    this.properties.placement.value = placement;
    const approval = Boolean(this.root.querySelector('[data-ct-approval]:checked'));
    this.properties.approval.value = approval ? 'Yes' : '';

    const iconDetailComplete = design === 'Dog' ? Boolean(personalization) : true;
    const layoutComplete = type !== 'Monogram' || design.includes('Stacked') || Boolean(monogramLayout);
    const monogramConfirmed = type !== 'Monogram' || Boolean(this.root.querySelector('[data-ct-monogram-confirm]:checked'));
    const choicesComplete = Boolean(type && design && thread && placement && (['Monogram', 'Established', 'Place'].includes(type) ? personalization : true) && iconDetailComplete && layoutComplete && monogramConfirmed);
    const complete = choicesComplete && approval;
    this.complete = complete;
    this.updatePreview();
    if (complete) this.error.hidden = true;
    const review = this.root.querySelector('[data-ct-review]');
    review.hidden = !choicesComplete;
    if (!choicesComplete) return;
    this.root.querySelector('[data-ct-review-title]').textContent = personalization || design;
    this.root.querySelector('[data-ct-review-detail]').textContent = [design !== personalization ? design : '', monogramLayout, thread, placement].filter(Boolean).join(' · ');
    const selectedThread = this.root.querySelector('[data-ct-thread]:checked')?.closest('label')?.querySelector('span');
    if (selectedThread) this.root.querySelector('[data-ct-review-swatch]').style.background = getComputedStyle(selectedThread).backgroundColor;
  }

  async addCustomizedItems(event) {
    if (!this.toggle.checked || this.submittingCustomizedItems) return;
    if (!this.validate(event)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.submittingCustomizedItems = true;

    const button = this.form.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    const formData = new FormData(this.form);
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value) properties[match[1]] = value;
    }
    const quantity = Number(formData.get('quantity') || 1);
    const baseVariantId = Number(formData.get('id'));
    const addonVariantId = Number(this.root.dataset.addonVariantId);
    const baseProduct = this.root.dataset.baseProduct || 'Common Tiger product';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: addonVariantId, quantity, properties: { ...properties, 'Applies to': baseProduct } },
            { id: baseVariantId, quantity, properties },
          ],
        }),
      });
      if (!response.ok) throw new Error('Unable to add embroidery');
      window.location.assign('/cart');
    } catch (error) {
      this.error.textContent = 'We could not add the embroidery to your cart. Please try again.';
      this.error.hidden = false;
      this.submittingCustomizedItems = false;
      if (button) button.disabled = false;
    }
  }

  orderProperties(extra = {}) {
    this.update();
    return {
      Embroidery: this.properties.enabled.value,
      'Embroidery type': this.properties.type.value,
      'Embroidery design': this.properties.design.value,
      Personalization: this.properties.personalization.value,
      'Thread color': this.properties.thread.value,
      'Embroidery placement': this.properties.placement.value,
      'Customer approval': this.properties.approval.value,
      ...extra,
    };
  }
}

class CTDuoEmbroideryManager {
  constructor(root) {
    this.root = root;
    this.form = document.getElementById(root.dataset.formId);
    this.primary = new CTEmbroideryCustomizer(root, { skipSubmit: true });
    this.instances = [this.primary];
    this.mode = 'one';
    this.insertModeSelector();
    this.form?.addEventListener('submit', (event) => this.addCustomizedItems(event), true);
  }

  insertModeSelector() {
    const selector = document.createElement('div');
    selector.className = 'ct-duo-selector';
    selector.innerHTML = `
      <div class="ct-embroidery__step-heading"><span>1</span><h4>Choose how many chairs</h4></div>
      <div class="ct-duo-selector__choices" role="radiogroup" aria-label="Number of chairs to embroider">
        <label><input type="radio" name="ct-duo-mode" value="one" checked><span><b>One chair</b><small>+$45</small></span></label>
        <label><input type="radio" name="ct-duo-mode" value="same"><span><b>Both chairs</b><small>Same design · +$90</small></span></label>
        <label><input type="radio" name="ct-duo-mode" value="different"><span><b>Both chairs</b><small>Different designs · +$90</small></span></label>
      </div>`;
    this.primary.builder.prepend(selector);
    this.primary.builder.querySelectorAll('.ct-embroidery__step:not(.ct-duo-selector) .ct-embroidery__step-heading > span').forEach((number) => {
      const value = Number(number.textContent);
      if (value) number.textContent = String(value + 1);
    });
    selector.addEventListener('change', (event) => this.setMode(event.target.value));
    this.updatePrice();
  }

  setMode(mode) {
    this.mode = mode;
    if (mode === 'different') this.addSecondCustomizer();
    else this.removeSecondCustomizer();
    this.updatePrice();
  }

  updatePrice() {
    const priceNode = this.root.querySelector('.ct-embroidery__dek strong');
    if (priceNode) priceNode.textContent = this.mode === 'one' ? '+$45' : '+$90';
  }

  addSecondCustomizer() {
    if (this.secondaryRoot) return;
    const clone = this.root.cloneNode(true);
    clone.removeAttribute('data-ct-ready');
    clone.classList.add('ct-embroidery--duo-chair-two');
    const intro = clone.querySelector('.ct-embroidery__intro');
    if (intro) intro.hidden = true;
    clone.querySelector('.ct-duo-selector')?.remove();
    clone.querySelectorAll('input').forEach((input) => {
      if (input.name) input.name = `${input.name}-chair2`;
      if (input.type === 'radio' || input.type === 'checkbox') input.checked = false;
      else if (input.type !== 'hidden') input.value = '';
    });
    const heading = document.createElement('div');
    heading.className = 'ct-duo-chair-heading';
    heading.innerHTML = '<p>Chair 2</p><h3>Choose a different embroidery design</h3>';
    clone.prepend(heading);
    const toggle = clone.querySelector('[data-ct-toggle]');
    toggle.checked = true;
    const builder = clone.querySelector('[data-ct-builder]');
    builder.hidden = false;
    clone.querySelector('.ct-special-request')?.remove();
    const approval = clone.querySelector('.ct-embroidery__approval');
    if (approval) approval.hidden = true;
    this.root.insertAdjacentElement('afterend', clone);
    this.secondaryRoot = clone;
    this.secondary = new CTEmbroideryCustomizer(clone, { skipSubmit: true });
    this.secondary.onToggle();
    this.instances = [this.primary, this.secondary];
  }

  removeSecondCustomizer() {
    this.secondaryRoot?.remove();
    this.secondaryRoot = null;
    this.secondary = null;
    this.instances = [this.primary];
  }

  approveSecondary() {
    if (!this.secondary) return;
    const approved = this.primary.root.querySelector('[data-ct-approval]')?.checked || false;
    const secondaryApproval = this.secondary.root.querySelector('[data-ct-approval]');
    if (secondaryApproval) secondaryApproval.checked = approved;
    this.secondary.update();
  }

  async addCustomizedItems(event) {
    if (!this.primary.toggle.checked || this.submitting) return;
    this.approveSecondary();
    const valid = this.instances.every((instance) => instance.validate(event));
    if (!valid) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.submitting = true;

    const button = this.form.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    const formData = new FormData(this.form);
    const quantity = Number(formData.get('quantity') || 1);
    const baseVariantId = Number(formData.get('id'));
    const addonVariantId = Number(this.root.dataset.addonVariantId);
    const baseProduct = this.root.dataset.baseProduct || 'Daymaker Duo';
    const baseItem = {
      id: baseVariantId,
      quantity,
      properties: { 'Custom embroidery': this.mode === 'one' ? 'One chair' : 'Both chairs' },
    };
    const items = [];

    const addChair = (instance, chair) => items.push({
      id: addonVariantId,
      quantity,
      properties: instance.orderProperties({ Chair: chair, 'Applies to': baseProduct }),
    });
    addChair(this.primary, 'Chair 1');
    if (this.mode === 'same') addChair(this.primary, 'Chair 2');
    if (this.mode === 'different') addChair(this.secondary, 'Chair 2');
    items.push(baseItem);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error('Unable to add embroidery');
      window.location.assign('/cart');
    } catch (error) {
      this.primary.error.textContent = 'We could not add the embroidery to your cart. Please try again.';
      this.primary.error.hidden = false;
      this.submitting = false;
      if (button) button.disabled = false;
    }
  }
}

class CTCoverSetEmbroideryManager {
  constructor(root) {
    this.root = root;
    this.form = document.getElementById(root.dataset.formId);
    this.primary = new CTEmbroideryCustomizer(root, { skipSubmit: true });
    this.instances = [this.primary];
    this.count = 1;
    this.mode = 'same';
    this.selector = document.createElement('div');
    this.selector.className = 'ct-cover-selector';
    this.primary.builder.prepend(this.selector);
    this.primary.builder.querySelectorAll('.ct-embroidery__step:not(.ct-cover-selector) .ct-embroidery__step-heading > span').forEach((number) => {
      const value = Number(number.textContent);
      if (value) number.textContent = String(value + 1);
    });
    this.selector.addEventListener('change', (event) => this.onSelectorChange(event));
    document.addEventListener('input', (event) => { if (event.target.matches(`[name="quantity"][form="${root.dataset.formId}"]`)) this.refresh(); });
    document.addEventListener('change', (event) => { if (event.target.matches(`[name="quantity"][form="${root.dataset.formId}"]`)) this.refresh(); });
    this.form?.addEventListener('submit', (event) => this.addCustomizedItems(event), true);
    this.refresh();
  }

  quantity() { return Math.max(1, Number(new FormData(this.form).get('quantity') || 1)); }

  onSelectorChange(event) {
    if (event.target.matches('[data-ct-cover-count]')) {
      this.count = Number(event.target.value);
      if (this.count === 1) this.mode = 'same';
      this.render();
    }
    if (event.target.matches('[data-ct-cover-mode]')) this.mode = event.target.value;
    this.syncCustomizers();
  }

  refresh() {
    this.count = Math.min(this.count, Math.min(this.quantity(), 4));
    this.render();
    this.syncCustomizers();
  }

  render() {
    const available = Math.min(this.quantity(), 4);
    const choices = Array.from({ length: available }, (_, index) => {
      const value = index + 1;
      return `<label><input type="radio" name="ct-cover-count" value="${value}" data-ct-cover-count ${this.count === value ? 'checked' : ''}><span><b>${value === 1 ? 'One cover set' : `${value} cover sets`}</b><small>+$${value * 45}</small></span></label>`;
    }).join('');
    this.selector.innerHTML = `<div class="ct-embroidery__step-heading"><span>1</span><h4>Choose how many cover sets to embroider</h4></div><div class="ct-cover-selector__choices" role="radiogroup" aria-label="Number of cover sets to embroider">${choices}</div>${this.count > 1 ? `<div class="ct-cover-selector__mode"><p>Use the same design on each?</p><div class="ct-cover-selector__choices" role="radiogroup" aria-label="Cover set designs"><label><input type="radio" name="ct-cover-mode" value="same" data-ct-cover-mode ${this.mode === 'same' ? 'checked' : ''}><span><b>Same design</b></span></label><label><input type="radio" name="ct-cover-mode" value="different" data-ct-cover-mode ${this.mode === 'different' ? 'checked' : ''}><span><b>Different designs</b></span></label></div></div>` : ''}${this.quantity() > 4 ? '<p class="ct-cover-selector__note">For embroidery on more than four cover sets, please email <a href="mailto:support@commontiger.com">support@commontiger.com</a>.</p>' : ''}`;
    const price = this.root.querySelector('.ct-embroidery__dek strong');
    if (price) price.textContent = `+$${this.count * 45}`;
  }

  syncCustomizers() {
    const needed = this.mode === 'different' ? this.count : 1;
    while (this.instances.length < needed) this.addCustomizer(this.instances.length + 1);
    while (this.instances.length > needed) this.instances.pop().root.remove();
  }

  addCustomizer(number) {
    const clone = this.root.cloneNode(true);
    clone.removeAttribute('data-ct-ready');
    clone.classList.add('ct-embroidery--cover-additional');
    clone.querySelector('.ct-cover-selector')?.remove();
    const intro = clone.querySelector('.ct-embroidery__intro');
    if (intro) intro.hidden = true;
    clone.querySelectorAll('input').forEach((input) => {
      if (input.name) input.name = `${input.name}-set${number}`;
      if (input.type === 'radio' || input.type === 'checkbox') input.checked = false;
      else if (input.type !== 'hidden') input.value = '';
    });
    const heading = document.createElement('div');
    heading.className = 'ct-duo-chair-heading';
    heading.innerHTML = `<p>Cover set ${number}</p><h3>Choose its embroidery design</h3>`;
    clone.prepend(heading);
    const toggle = clone.querySelector('[data-ct-toggle]');
    toggle.checked = true;
    clone.querySelector('[data-ct-builder]').hidden = false;
    clone.querySelector('.ct-special-request')?.remove();
    const approval = clone.querySelector('.ct-embroidery__approval');
    if (approval) approval.hidden = true;
    this.instances[this.instances.length - 1].root.insertAdjacentElement('afterend', clone);
    const instance = new CTEmbroideryCustomizer(clone, { skipSubmit: true });
    instance.onToggle();
    this.instances.push(instance);
  }

  approveAll() {
    const approved = this.primary.root.querySelector('[data-ct-approval]')?.checked || false;
    this.instances.slice(1).forEach((instance) => {
      const approval = instance.root.querySelector('[data-ct-approval]');
      if (approval) approval.checked = approved;
      instance.update();
    });
  }

  async addCustomizedItems(event) {
    if (!this.primary.toggle.checked || this.submitting) return;
    this.approveAll();
    if (!this.instances.every((instance) => instance.validate(event))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.submitting = true;
    const button = this.form.querySelector('[type="submit"]');
    if (button) button.disabled = true;
    const formData = new FormData(this.form);
    const quantity = this.quantity();
    const baseVariantId = Number(formData.get('id'));
    const addonVariantId = Number(this.root.dataset.addonVariantId);
    const baseProduct = this.root.dataset.baseProduct || 'Daymaker cover set';
    const baseItem = { id: baseVariantId, quantity, properties: { 'Custom embroidery': `${this.count} of ${quantity} cover sets` } };
    const items = [];
    if (this.mode === 'same') items.push({ id: addonVariantId, quantity: this.count, properties: this.primary.orderProperties({ 'Cover sets': `1–${this.count}`, 'Applies to': baseProduct }) });
    else this.instances.forEach((instance, index) => items.push({ id: addonVariantId, quantity: 1, properties: instance.orderProperties({ 'Cover set': String(index + 1), 'Applies to': baseProduct }) }));
    items.push(baseItem);
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      if (!response.ok) throw new Error('Unable to add embroidery');
      window.location.assign('/cart');
    } catch (error) {
      this.primary.error.textContent = 'We could not add the embroidery to your cart. Please try again.';
      this.primary.error.hidden = false;
      this.submitting = false;
      if (button) button.disabled = false;
    }
  }
}

const initializeCTEmbroidery = (scope = document) => {
  scope.querySelectorAll('[data-ct-embroidery]:not([data-ct-ready])').forEach((root) => {
    root.dataset.ctReady = 'true';
    const isDuo = root.dataset.productHandle === 'single-duo';
    const isCoverSet = root.dataset.productHandle === 'the-daymaker-cover-set-color-swap';
    if (isDuo) new CTDuoEmbroideryManager(root);
    else if (isCoverSet) new CTCoverSetEmbroideryManager(root);
    else new CTEmbroideryCustomizer(root);
  });
};

document.addEventListener('DOMContentLoaded', () => initializeCTEmbroidery());
document.addEventListener('shopify:section:load', (event) => initializeCTEmbroidery(event.target));
document.addEventListener('shopify:block:select', (event) => initializeCTEmbroidery(event.target.closest('[data-ct-embroidery]')?.parentElement || document));
